import React, { useCallback, useEffect, useState } from "react";
import { useApi } from "../hooks/useApi";
import AuditFindings from "./AuditFindings";
import { DownloadButton } from "./AuditReports";

// One audit engagement's fieldwork (Audit Management 5A step 8): Start fieldwork freezes the scope into
// one test per requirement; each test is worked by a preparer, sent to review, sent back with a note,
// or proposed as Not tested for someone else to decide; the lead changes scope with a reason; every
// step is in the history. The server sends, per test, what this person may do now (canPrepare,
// canReview); the screen only offers those actions.

export const TEST_STATE_LABEL = {
    NotStarted: "Not started",
    InProgress: "In progress",
    ReadyForReview: "Ready for review",
    ChangesRequested: "Changes requested",
    SignedOff: "Signed off",
    Reopened: "Reopened",
    NotTestedPending: "Not tested — awaiting approval",
    NotTested: "Not tested",
    OutOfScope: "Out of scope",
};

export const RESULT_LABEL = {
    Effective: "Effective",
    PartiallyEffective: "Partially effective",
    Ineffective: "Ineffective",
};

const STATE_STYLE = {
    NotStarted: "bg-gray-50 text-gray-600 border-gray-200",
    InProgress: "bg-blue-50 text-blue-700 border-blue-200",
    ReadyForReview: "bg-amber-50 text-amber-700 border-amber-200",
    ChangesRequested: "bg-red-50 text-red-700 border-red-200",
    SignedOff: "bg-green-50 text-green-700 border-green-200",
    Reopened: "bg-blue-50 text-blue-700 border-blue-200",
    NotTestedPending: "bg-amber-50 text-amber-700 border-amber-200",
    NotTested: "bg-gray-50 text-gray-500 border-gray-200",
    OutOfScope: "bg-gray-50 text-gray-400 border-gray-200",
};

// Progress counts the audit's own tests; follow-up tests of findings are listed under their finding.
export const progressOf = (tests) => {
    const inScope = tests.filter(t => t.kind !== "FollowUp" && t.state !== "OutOfScope");
    const done = inScope.filter(t => t.state === "SignedOff" || t.state === "NotTested").length;
    return { done, total: inScope.length };
};

const messageOf = (err, fallback) => err?.response?.data?.message || fallback;

// Full class names, so Tailwind keeps them.
const TONE = {
    indigo: "border-indigo-200 text-indigo-700 hover:bg-indigo-50",
    amber: "border-amber-200 text-amber-700 hover:bg-amber-50",
    red: "border-red-200 text-red-700 hover:bg-red-50",
    gray: "border-gray-200 text-gray-700 hover:bg-gray-100",
};

// A one-line text prompt inside the row: a reason or note, then the action.
const NoteAction = ({ label, placeholder, onSubmit, tone = "indigo" }) => {
    const [open, setOpen] = useState(false);
    const [note, setNote] = useState("");
    if (!open)
        return <button type="button" onClick={() => setOpen(true)} className={`text-xs px-2 py-0.5 border rounded ${TONE[tone] || TONE.indigo}`}>{label}</button>;
    return (
        <span className="inline-flex flex-wrap items-center gap-1">
            <input aria-label={placeholder} placeholder={placeholder} value={note} onChange={e => setNote(e.target.value)} maxLength={4000}
                className="text-xs border border-gray-200 rounded px-1.5 py-0.5 w-64" />
            {/* Closes only when the action went through; on a refusal the note stays for another try. */}
            <button type="button" disabled={!note.trim()} onClick={() => onSubmit(note).then(() => { setOpen(false); setNote(""); }, () => {})}
                className="text-xs px-2 py-0.5 rounded bg-indigo-600 text-white disabled:opacity-50">{label}</button>
            <button type="button" onClick={() => setOpen(false)} className="text-xs text-gray-500 hover:underline">Cancel</button>
        </span>
    );
};

const VERSION_STATE_LABEL = { Captured: "Under review", Signed: "Signed", Returned: "Sent back" };
const formatBytes = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : n >= 1024 ? `${Math.round(n / 1024)} KB` : `${n} B`);

const REQUEST_STATUS_STYLE = {
    Satisfied: "bg-green-50 text-green-700 border-green-200",
    Pending: "bg-gray-50 text-gray-600 border-gray-200",
    Overdue: "bg-red-50 text-red-700 border-red-200",
    Expired: "bg-amber-50 text-amber-700 border-amber-200",
};

// Evidence asked of someone for this test, through Evidence Collection: they get the usual email and
// reminders and upload on their Evidence Requests page; the request is already in the workpapers, so
// what they upload is captured at Send to review.
export const AuditEvidenceRequests = ({ engagementId, test, canPrepare, onRaised }) => {
    const apiCall = useApi();
    const base = `/security-insights/audits/${engagementId}/tests/${test.id}/evidence-requests`;
    const [items, setItems] = useState(null);
    const [people, setPeople] = useState(null);
    const [form, setForm] = useState(null); // null = closed
    const [error, setError] = useState(null);

    const load = useCallback(() => apiCall("GET", base)
        .then(list => setItems(Array.isArray(list) ? list : []))
        .catch(() => setItems([])), [apiCall, base]);
    useEffect(() => { load(); }, [load]);

    const open = () => {
        setForm({ title: "", description: "", ownerUserId: "", dueDate: "" });
        if (people == null)
            apiCall("GET", `/security-insights/audits/${engagementId}/people`)
                .then(list => setPeople(Array.isArray(list) ? list : []))
                .catch(() => setPeople([]));
    };
    const submit = async () => {
        setError(null);
        try {
            await apiCall("POST", base, {
                title: form.title, description: form.description || null,
                ownerUserId: Number(form.ownerUserId), dueDate: form.dueDate,
            });
            setForm(null);
            await load();
            onRaised?.();
        } catch (err) {
            setError(messageOf(err, "The request could not be raised. Try again."));
        }
    };
    const set = (patch) => setForm(f => ({ ...f, ...patch }));

    if (items == null) return null;
    return (
        <div className="space-y-1" data-testid={`audit-evidence-requests-${test.requirementId}`}>
            <div className="font-semibold text-gray-600">Evidence requests</div>
            {items.length === 0 && !form && <div className="text-gray-400">No evidence requested for this test.</div>}
            <ul className="space-y-0.5">
                {items.map(r => (
                    <li key={r.evidenceRequestId} className="flex flex-wrap items-center gap-2">
                        <span className="text-gray-800">{r.title}</span>
                        <span className={`px-1.5 py-0.5 rounded border font-semibold ${REQUEST_STATUS_STYLE[r.status] || REQUEST_STATUS_STYLE.Pending}`}>{r.status}</span>
                        <span className="text-gray-500">{r.ownerName || "No owner"} · due {new Date(r.dueDate).toLocaleDateString()}</span>
                        <span className="text-gray-400">{r.documentCount} document{r.documentCount === 1 ? "" : "s"}</span>
                    </li>
                ))}
            </ul>
            {canPrepare && !form && (
                <button type="button" onClick={open} className="px-2 py-0.5 border border-indigo-200 rounded text-indigo-700 hover:bg-indigo-50">Request evidence</button>
            )}
            {form && (
                <div className="border border-gray-200 rounded p-2 bg-white grid grid-cols-1 md:grid-cols-2 gap-2">
                    <label className="flex flex-col gap-1">What is needed
                        <input value={form.title} onChange={e => set({ title: e.target.value })} maxLength={300} className="border border-gray-200 rounded px-2 py-1" />
                    </label>
                    <label className="flex flex-col gap-1">From
                        <select value={form.ownerUserId} onChange={e => set({ ownerUserId: e.target.value })} className="border border-gray-200 rounded px-2 py-1 bg-white">
                            <option value="">Choose the owner</option>
                            {(people || []).map(p => <option key={p.id} value={p.id}>{p.fullName}</option>)}
                        </select>
                    </label>
                    <label className="flex flex-col gap-1">Due by
                        <input type="date" value={form.dueDate} onChange={e => set({ dueDate: e.target.value })} className="border border-gray-200 rounded px-2 py-1" />
                    </label>
                    <label className="flex flex-col gap-1">Details (optional)
                        <input value={form.description} onChange={e => set({ description: e.target.value })} maxLength={1500} className="border border-gray-200 rounded px-2 py-1" />
                    </label>
                    <div className="flex gap-2 md:col-span-2">
                        <button type="button" disabled={!form.title.trim() || !form.ownerUserId || !form.dueDate} onClick={submit}
                            className="px-2 py-0.5 rounded bg-indigo-600 text-white disabled:opacity-50">Send request</button>
                        <button type="button" onClick={() => { setForm(null); setError(null); }} className="text-gray-500 hover:underline">Cancel</button>
                    </div>
                </div>
            )}
            {error && <div className="text-red-600" role="alert">{error}</div>}
        </div>
    );
};

// A test's workpapers: the working set the preparer attaches (captured at Send to review), and every
// captured version with its files; a captured file downloads only while it matches its hash.
export const TestWorkpapers = ({ engagementId, test, requirement, policies, canPrepare, evidenceRequestsEnabled = false }) => {
    const apiCall = useApi();
    const base = `/security-insights/audits/${engagementId}/tests/${test.id}`;
    const [papers, setPapers] = useState({ loading: true, working: [], versions: [] });
    const [error, setError] = useState(null);

    const load = useCallback(() => apiCall("GET", `${base}/workpapers`)
        .then(d => setPapers({ loading: false, working: d?.working || [], versions: d?.versions || [] }))
        .catch(() => setPapers(p => ({ ...p, loading: false }))), [apiCall, base]);
    useEffect(() => { load(); }, [load]);

    const run = async (fn) => {
        setError(null);
        try { await fn(); await load(); } catch (err) { setError(messageOf(err, "That did not go through. Try again.")); }
    };
    const upload = (file) => run(() => {
        const data = new FormData();
        data.append("file", file);
        return apiCall("POST", `${base}/workpapers/file`, data, true);
    });
    const link = (value) => {
        const [kind, ...rest] = value.split("|");
        return run(() => apiCall("POST", `${base}/workpapers/link`, { kind, sourceId: rest.join("|") }));
    };
    const download = async (version, item) => {
        setError(null);
        try {
            const blob = await apiCall("GET", `${base}/versions/${version.id}/items/${item.id}/file`, null, false, "blob");
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = item.fileName || "workpaper";
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(messageOf(err, "This file could not be downloaded."));
        }
    };

    const linked = new Set(papers.working.map(w => `${w.kind}|${w.sourceId}`));
    const options = [
        ...(requirement?.controls || []).map(c => ({ value: `ControlEvidence|${c.controlId}`, label: `Control evidence: ${c.title}` })),
        ...(requirement?.evidenceRequests || []).map(r => ({ value: `EvidenceRequest|${r.id}`, label: `Evidence request: ${r.title}` })),
        ...(policies || []).map(p => ({ value: `PolicyVersion|${p.id}:${p.version}`, label: `Policy: ${p.title || p.fileName} (version ${p.version})` })),
    ].filter(o => !linked.has(o.value));

    if (papers.loading) return <div className="text-gray-400">Loading workpapers…</div>;
    return (
        <div className="space-y-2" data-testid={`audit-workpapers-${test.requirementId}`}>
            <div className="font-semibold text-gray-600">Workpapers</div>
            {papers.working.length === 0
                ? <div className="text-gray-400">Nothing attached yet.</div>
                : (
                    <ul className="space-y-0.5">
                        {papers.working.map(w => (
                            <li key={w.id} className="flex items-center gap-2">
                                <span className="text-gray-800">{w.label}</span>
                                {w.size > 0 && <span className="text-gray-400">{formatBytes(w.size)}</span>}
                                {canPrepare && (
                                    <button type="button" onClick={() => run(() => apiCall("DELETE", `${base}/workpapers/${w.id}`))}
                                        className="text-gray-500 hover:text-red-600 hover:underline">Remove</button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            {canPrepare && (
                <div className="flex flex-wrap items-center gap-2">
                    <label className="px-2 py-0.5 border border-gray-200 rounded cursor-pointer hover:bg-gray-100">
                        Attach a file
                        <input type="file" accept=".pdf,.docx,.txt,.csv" className="hidden" aria-label="Attach a file"
                            onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(f); }} />
                    </label>
                    {options.length > 0 && (
                        <select aria-label="Link evidence" value="" onChange={e => e.target.value && link(e.target.value)}
                            className="border border-gray-200 rounded px-1.5 py-0.5 bg-white">
                            <option value="">Link evidence…</option>
                            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    )}
                    <span className="text-gray-400">Sending to review captures a copy of everything listed here.</span>
                </div>
            )}
            {evidenceRequestsEnabled && (
                <AuditEvidenceRequests engagementId={engagementId} test={test} canPrepare={canPrepare} onRaised={load} />
            )}

            {papers.versions.map(v => (
                <div key={v.id} className="border border-gray-200 rounded p-2 bg-white" data-testid={`audit-version-${test.requirementId}-${v.number}`}>
                    <div className="flex flex-wrap gap-x-3 text-gray-600">
                        <span className="font-semibold text-gray-800">Version {v.number}</span>
                        <span>{VERSION_STATE_LABEL[v.state] || v.state}</span>
                        <span>Captured {new Date(v.capturedAt).toLocaleString()}{v.capturedByName ? ` by ${v.capturedByName}` : ""}</span>
                        {v.signedAt && <span>Signed {new Date(v.signedAt).toLocaleString()}{v.signedByName ? ` by ${v.signedByName}` : ""}</span>}
                    </div>
                    <ul className="mt-1 space-y-0.5">
                        {v.items.length === 0 && <li className="text-gray-400">No files.</li>}
                        {v.items.map(i => (
                            <li key={i.id} className="flex flex-wrap items-center gap-2">
                                <span className="text-gray-800">{i.fileName || i.label}</span>
                                <span className="text-gray-400">{formatBytes(i.size)}</span>
                                {i.changedSinceCapture && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">Changed since capture</span>}
                                <DownloadButton onDownload={() => download(v, i)} />
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
            {error && <div className="text-red-600" role="alert">{error}</div>}
        </div>
    );
};

const TestRow = ({ test, open, onToggle, act, canManage, active, engagementId, requirement, policies, evidenceRequestsEnabled }) => {
    const [form, setForm] = useState({ procedure: test.procedure || "", result: test.result || "", conclusion: test.conclusion || "" });
    useEffect(() => { setForm({ procedure: test.procedure || "", result: test.result || "", conclusion: test.conclusion || "" }); }, [test]);
    const set = (patch) => setForm(f => ({ ...f, ...patch }));
    const base = `tests/${test.id}`;
    // Buttons: the error is shown by the fieldwork panel; nothing more to do here.
    const run = (method, path, body) => act(method, path, body).catch(() => {});
    const followUp = test.kind === "FollowUp";
    // A follow-up shares its requirement with the original test, so it is identified by its own id.
    const testKey = followUp ? `followup-${test.id}` : test.requirementId;
    return (
        <li data-testid={`audit-test-${testKey}`} className={open ? "bg-gray-50" : ""}>
            <button type="button" onClick={onToggle} aria-expanded={open}
                className="w-full text-left px-3 py-2 flex flex-wrap items-start gap-x-3 gap-y-1 hover:bg-gray-50 text-xs">
                <span className="font-semibold text-gray-800 w-28 shrink-0">{followUp ? `Follow-up ${test.sequence}` : test.requirementDisplayId}</span>
                <span className="text-gray-600 flex-1 min-w-[12rem]">{test.requirementTitle}</span>
                <span className="text-gray-500 w-32 shrink-0">{test.preparerName || "—"}</span>
                <span className={`px-2 py-0.5 rounded-full border font-semibold ${STATE_STYLE[test.state] || STATE_STYLE.NotStarted}`}>{TEST_STATE_LABEL[test.state] || test.state}</span>
            </button>
            {open && (
                <div className="px-3 pb-3 space-y-2 text-xs" data-testid={`audit-test-detail-${testKey}`}>
                    {test.requirementVersion && <div className="text-gray-500">Version {test.requirementVersion}</div>}
                    {test.reviewNote && <div className="text-red-700">Reviewer: {test.reviewNote}</div>}
                    {test.notTestedReason && (test.state === "NotTestedPending" || test.state === "NotTested") && <div className="text-gray-700">Not tested because: {test.notTestedReason}</div>}
                    {test.outOfScopeReason && test.state === "OutOfScope" && <div className="text-gray-700">Out of scope: {test.outOfScopeReason}</div>}

                    {test.canPrepare ? (
                        <div className="space-y-2">
                            <label className="flex flex-col gap-1">Test procedure
                                <textarea value={form.procedure} onChange={e => set({ procedure: e.target.value })} rows={3} className="border border-gray-200 rounded px-2 py-1" />
                            </label>
                            <label className="flex flex-col gap-1 w-64">Result
                                <select value={form.result} onChange={e => set({ result: e.target.value })} className="border border-gray-200 rounded px-2 py-1 bg-white">
                                    <option value="">Not decided</option>
                                    {Object.entries(RESULT_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                            </label>
                            <label className="flex flex-col gap-1">Conclusion
                                <textarea value={form.conclusion} onChange={e => set({ conclusion: e.target.value })} rows={2} className="border border-gray-200 rounded px-2 py-1" />
                            </label>
                            <div className="flex flex-wrap gap-2">
                                <button type="button" onClick={() => run("PUT", base, form)} className="px-2 py-0.5 border border-gray-200 rounded hover:bg-gray-100">Save</button>
                                {test.state !== "NotStarted" && (
                                    <button type="button" onClick={() => run("POST", `${base}/submit`)} className="px-2 py-0.5 rounded bg-indigo-600 text-white">Send to review</button>
                                )}
                                <NoteAction label="Propose Not tested" placeholder="Why will it not be tested?" tone="amber"
                                    onSubmit={(note) => act("POST", `${base}/propose-not-tested`, { note })} />
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-1 text-gray-700">
                            <div><span className="text-gray-500">Procedure:</span> {test.procedure || "—"}</div>
                            <div><span className="text-gray-500">Result:</span> {RESULT_LABEL[test.result] || "—"}</div>
                            <div><span className="text-gray-500">Conclusion:</span> {test.conclusion || "—"}</div>
                        </div>
                    )}

                    {/* Re-mounted per captured version, so a new capture shows at once. */}
                    <TestWorkpapers key={test.currentVersion} engagementId={engagementId} test={test}
                        requirement={requirement} policies={policies} canPrepare={test.canPrepare}
                        evidenceRequestsEnabled={evidenceRequestsEnabled} />

                    {test.canReview && test.state === "ReadyForReview" && (
                        <div className="flex flex-wrap gap-2">
                            <button type="button" onClick={() => run("POST", `${base}/sign-off`)}
                                className="px-2 py-0.5 rounded bg-green-600 text-white">Sign off version {test.currentVersion}</button>
                            <NoteAction label="Request changes" placeholder="What needs to change?" tone="red"
                                onSubmit={(note) => act("POST", `${base}/request-changes`, { note })} />
                        </div>
                    )}
                    {test.canReopen && (
                        <NoteAction label="Reopen" placeholder="Why reopen this signed-off test?" tone="amber"
                            onSubmit={(note) => act("POST", `${base}/reopen`, { note })} />
                    )}
                    {test.canReview && test.state === "NotTestedPending" && (
                        <div className="flex flex-wrap gap-2">
                            <button type="button" onClick={() => run("POST", `${base}/decide-not-tested`, { approve: true })}
                                className="px-2 py-0.5 rounded bg-indigo-600 text-white">Approve Not tested</button>
                            <NoteAction label="Reject" placeholder="Why must it be tested?" tone="red"
                                onSubmit={(note) => act("POST", `${base}/decide-not-tested`, { approve: false, note })} />
                        </div>
                    )}
                    {canManage && active && !followUp && !["SignedOff", "OutOfScope"].includes(test.state) && (
                        <NoteAction label="Take out of scope" placeholder="Reason for the scope change" tone="gray"
                            onSubmit={(reason) => act("POST", "scope", { action: "Remove", requirementId: test.requirementId, reason })} />
                    )}
                    {canManage && active && !followUp && test.state === "OutOfScope" && (
                        <NoteAction label="Bring back into scope" placeholder="Reason for the scope change" tone="gray"
                            onSubmit={(reason) => act("POST", "scope", { action: "Add", requirementId: test.requirementId, reason })} />
                    )}
                </div>
            )}
        </li>
    );
};

// canViewRequirements: org-wide GRC access (useRBAC canViewAllGrcData); without it the framework's
// requirement list is not read (the server answers 403), so the audit test rows show no requirement detail.
const AuditFieldwork = ({ engagement, onEngagementChanged, evidenceRequestsEnabled = false, canViewRequirements = false }) => {
    const apiCall = useApi();
    const base = `/security-insights/audits/${engagement.id}`;
    const [tests, setTests] = useState({ loading: engagement.status !== "Planning", failed: false, items: [] });
    const [openId, setOpenId] = useState(null);
    const [error, setError] = useState(null);
    const [history, setHistory] = useState(null);
    const active = engagement.status === "Fieldwork" || engagement.status === "Review";

    const load = useCallback(() => {
        if (engagement.status === "Planning") return Promise.resolve();
        return apiCall("GET", `${base}/tests`)
            .then(items => setTests({ loading: false, failed: false, items: Array.isArray(items) ? items : [] }))
            .catch(() => setTests(s => ({ ...s, loading: false, failed: true })));
    }, [apiCall, base, engagement.status]);
    useEffect(() => { load(); }, [load]);

    // What a preparer can link: each requirement's controls and evidence requests (one read of the
    // framework's requirements), and the organization's policies.
    const [requirements, setRequirements] = useState(new Map());
    const [policies, setPolicies] = useState([]);
    useEffect(() => {
        if (engagement.status === "Planning") return;
        if (canViewRequirements) apiCall("GET", `/security-insights/framework-requirements?framework=${encodeURIComponent(engagement.framework || "")}`)
            .then(d => setRequirements(new Map((d?.requirements || []).map(r => [r.id, r]))))
            .catch(() => setRequirements(new Map()));
        apiCall("GET", "/security-insights/policy-docs")
            .then(list => setPolicies(Array.isArray(list) ? list : []))
            .catch(() => setPolicies([]));
    }, [apiCall, canViewRequirements, engagement.framework, engagement.status]);

    // Every action: call, then reload the tests (an action can change several), and the history if shown.
    const act = async (method, path, body) => {
        setError(null);
        try {
            await apiCall(method, `${base}/${path}`, body);
            await load();
            if (history) setHistory(await apiCall("GET", `${base}/activity`));
        } catch (err) {
            setError(messageOf(err, "That did not go through. Try again."));
            throw err;
        }
    };

    const startFieldwork = async () => {
        setError(null);
        try {
            await apiCall("POST", `${base}/start-fieldwork`);
            await onEngagementChanged();
        } catch (err) {
            setError(messageOf(err, "Fieldwork could not start. Try again."));
        }
    };

    if (engagement.status === "Planning")
        return (
            <div className="text-xs space-y-1" data-testid={`audit-fieldwork-${engagement.id}`}>
                <div className="text-gray-500">Starting fieldwork fixes the scope: each requirement in scope becomes a test, with its text as it is today.</div>
                {engagement.canManage && (
                    <button type="button" onClick={startFieldwork} className="px-3 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700">Start fieldwork</button>
                )}
                {error && <div className="text-red-600" role="alert">{error}</div>}
            </div>
        );

    const { done, total } = progressOf(tests.items);
    const row = (t) => (
        <TestRow key={t.id} test={t} open={openId === t.id} onToggle={() => setOpenId(openId === t.id ? null : t.id)}
            act={act} canManage={engagement.canManage} active={active} engagementId={engagement.id}
            requirement={requirements.get(t.requirementId)} policies={policies}
            evidenceRequestsEnabled={evidenceRequestsEnabled} />
    );
    return (
        <div className="text-xs space-y-2" data-testid={`audit-fieldwork-${engagement.id}`}>
            <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-gray-600 mr-auto">Tests — {done} of {total} complete</span>
                <button type="button" onClick={async () => setHistory(history ? null : await apiCall("GET", `${base}/activity`))}
                    className="px-2 py-0.5 border border-gray-200 rounded hover:bg-gray-100">{history ? "Hide history" : "Show history"}</button>
            </div>
            {error && <div className="text-red-600" role="alert">{error}</div>}
            {tests.loading ? <div className="text-gray-400">Loading tests…</div>
                : tests.failed ? <div className="text-red-600" role="alert">Could not load the tests. Try again.</div>
                : (
                    <ul className="divide-y divide-gray-100 border border-gray-200 rounded bg-white">
                        {tests.items.filter(t => t.kind !== "FollowUp").map(row)}
                    </ul>
                )}
            {!tests.loading && !tests.failed && (
                <AuditFindings engagement={engagement} tests={tests.items} onChanged={load} renderFollowUp={row} />
            )}
            {history && (
                <ul className="border border-gray-200 rounded bg-white divide-y divide-gray-100" data-testid={`audit-history-${engagement.id}`}>
                    {history.map((h, i) => (
                        <li key={i} className="px-3 py-1 flex gap-3">
                            <span className="text-gray-500 w-40 shrink-0">{new Date(h.at).toLocaleString()}</span>
                            <span className="text-gray-700 w-40 shrink-0">{h.actorName || "Someone"}</span>
                            <span className="text-gray-800">{h.action}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default AuditFieldwork;
